import Phaser from 'phaser';
import { Boss, BossConfig, BossPhase } from '../base/Boss';
import { Allegiance } from '../../constants/enums';

/**
 * Fire point positions relative to sprite center
 */
interface FirePoint {
  x: number;
  y: number;
}

/**
 * Extended phase config for Scarecrow boss
 */
interface ScarecrowPhase extends BossPhase {
  orbCount?: number;
  batCount?: number;
}

/**
 * Subjefelevel1 - CyberScareCrow boss (Level 1 sub-boss)
 *
 * Phases (based on HP %):
 * - 100%: 1 orb (easy start)
 * - 80%: 2 orbs
 * - 60%: 3 orbs
 * - 50%: 3 orbs + 1 bat (first bat)
 * - 25%: 4 orbs + 1 bat
 * - 10%: 4 orbs + 2 bats (final phase)
 *
 * On death:
 * - Drops UpDoubleLaser powerup
 * - Destroys all DestructableHay objects
 */
export class Subjefelevel1 extends Boss {
  // Fire points (relative to center, will flip when facing left)
  private firePoints: Record<string, FirePoint> = {
    leftHand: { x: -40, y: -60 },
    rightHand: { x: 40, y: -60 },
  };

  // Attack state
  private attackTimer: number = 0;
  private attackCooldown: number = 2500; // ms between attack sequences

  // Orb attack state
  private orbBurstCount: number = 0;
  private orbBurstMax: number = 1;       // Single burst per attack (orbCount per burst)
  private orbBurstDelay: number = 800;   // ms between bursts
  private orbBurstTimer: number = 0;
  private isOrbBursting: boolean = false;

  // Bat spawning state
  private isCharging: boolean = false;
  private chargeTimer: number = 0;
  private chargeDuration: number = 1200; // ms
  private batSpawnCount: number = 0;
  private batsToSpawn: number = 0;
  private batSpawnTimer: number = 0;
  private batSpawnInterval: number = 400; // ms between bat spawns
  private isSpawningBats: boolean = false;
  private nextBatHand: 'left' | 'right' = 'left';

  // Phase tracking for tint
  private phaseIndex: number = 0;
  private readonly phaseTints: number[] = [
    0xffffff, // 100% - normal
    0xffeeee, // 80% - slightly red
    0xffdddd, // 60% - light red
    0xffaaaa, // 50% - more red
    0xff7777, // 25% - very red
    0xff4444, // 10% - intense red
  ];

  // Animation keys (must be lowercase to match animations-c3data.json)
  private animKeys = {
    idleRight: 'subjefelevel1_right',
    idleLeft: 'subjefelevel1_left',
    charging: 'subjefelevel1_charging',
    death: 'subjefelevel1_death',
  };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: BossConfig = {}
  ) {
    // Default phases if not provided in config
    const defaultPhases: ScarecrowPhase[] = [
      { hpPercent: 100, pattern: 'orbs', orbCount: 1 },
      { hpPercent: 80, pattern: 'orbs', orbCount: 2 },
      { hpPercent: 60, pattern: 'orbs', orbCount: 3 },
      { hpPercent: 50, pattern: 'mixed', orbCount: 3, batCount: 1 },
      { hpPercent: 25, pattern: 'mixed', orbCount: 4, batCount: 1 },
      { hpPercent: 10, pattern: 'mixed', orbCount: 4, batCount: 2 },
    ];

    super(scene, x, y, texture, {
      ...config,
      phases: config.phases || defaultPhases,
      hp: config.hp ?? 100,
      activationRange: config.activationRange ?? 700,
    });

    // Override fire points from config if provided
    const configFirePoints = (config as Record<string, unknown>).firePoints as Record<string, FirePoint> | undefined;
    if (configFirePoints) {
      this.firePoints = { ...this.firePoints, ...configFirePoints };
    }
  }

  /**
   * Called when player enters activation range
   */
  onActivate(): void {
    console.log('[Subjefelevel1] Boss activated!');
  }

  /**
   * Called when transitioning to a new phase
   */
  onPhaseChange(newPhase: BossPhase, oldPhase: BossPhase | undefined): void {
    const phase = newPhase as ScarecrowPhase;
    console.log(`[Subjefelevel1] Phase change: ${oldPhase?.pattern || 'none'} -> ${phase.pattern} (orbs: ${phase.orbCount}, bats: ${phase.batCount})`);

    // Reset attack state
    this.attackTimer = 0;
    this.isOrbBursting = false;
    this.isCharging = false;
    this.isSpawningBats = false;

    // Update phase index for tint
    this.phaseIndex++;
    this.applyPhaseTint();

    // If pattern includes bats, set up bat count
    if (phase.pattern === 'mixed' || phase.pattern === 'bats') {
      this.batsToSpawn = phase.batCount ?? 1;
    }
  }

  /**
   * Apply red tint based on current phase
   */
  private applyPhaseTint(): void {
    const tintIndex = Math.min(this.phaseIndex, this.phaseTints.length - 1);
    const tint = this.phaseTints[tintIndex];
    this.setTint(tint);
    console.log(`[Subjefelevel1] Applied tint: 0x${tint.toString(16)}`);
  }

  /**
   * Main update loop
   */
  update(time: number, delta: number): void {
    super.update(time, delta);

    if (!this.isActivated || this.isDead()) return;

    // Face the player
    this.facePlayer();

    // Get current phase config
    const phase = this.currentPhase as ScarecrowPhase | undefined;
    if (!phase) return;

    // Execute pattern based on current phase
    if (phase.pattern === 'orbs') {
      this.updateOrbPattern(delta, phase);
    } else if (phase.pattern === 'mixed') {
      this.updateMixedPattern(delta, phase);
    } else if (phase.pattern === 'bats') {
      this.updateBatsPattern(delta, phase);
    }
  }

  /**
   * Face toward the player
   */
  private facePlayer(): void {
    const player = this.scene.registry.get('player') as Phaser.GameObjects.Sprite | undefined;
    if (!player) return;

    const facingLeft = player.x < this.x;
    this.setFlipX(facingLeft);

    // Update idle animation based on facing (only if not in special state)
    if (!this.isCharging && !this.isSpawningBats) {
      const idleAnim = facingLeft ? this.animKeys.idleLeft : this.animKeys.idleRight;
      if (this.anims.currentAnim?.key !== idleAnim) {
        this.play(idleAnim, true);
      }
    }
  }

  // ==================== ORB PATTERN ====================

  /**
   * Update orb-only attack pattern
   */
  private updateOrbPattern(delta: number, phase: ScarecrowPhase): void {
    if (this.isOrbBursting) {
      this.orbBurstTimer += delta;
      if (this.orbBurstTimer >= this.orbBurstDelay) {
        this.orbBurstTimer = 0;
        this.shootOrbs(phase.orbCount ?? 2);
        this.orbBurstCount++;

        if (this.orbBurstCount >= this.orbBurstMax) {
          this.isOrbBursting = false;
          this.attackTimer = 0;
        }
      }
    } else {
      this.attackTimer += delta;
      if (this.attackTimer >= this.attackCooldown) {
        this.startOrbBurst();
      }
    }
  }

  /**
   * Update mixed pattern (orbs + bats)
   */
  private updateMixedPattern(delta: number, phase: ScarecrowPhase): void {
    if (this.isCharging) {
      // Charging for bat spawn
      this.chargeTimer += delta;
      if (this.chargeTimer >= this.chargeDuration) {
        this.finishCharging(phase);
      }
    } else if (this.isSpawningBats) {
      // Spawning bats
      this.batSpawnTimer += delta;
      if (this.batSpawnTimer >= this.batSpawnInterval) {
        this.batSpawnTimer = 0;
        this.spawnBat();
        this.batSpawnCount++;

        if (this.batSpawnCount >= this.batsToSpawn) {
          this.isSpawningBats = false;
          // After bats, shoot orbs
          this.shootOrbs(phase.orbCount ?? 4);
          this.attackTimer = 0;
        }
      }
    } else if (this.isOrbBursting) {
      // Orb burst in progress
      this.orbBurstTimer += delta;
      if (this.orbBurstTimer >= this.orbBurstDelay) {
        this.orbBurstTimer = 0;
        this.shootOrbs(phase.orbCount ?? 4);
        this.orbBurstCount++;

        if (this.orbBurstCount >= this.orbBurstMax) {
          this.isOrbBursting = false;
          this.attackTimer = 0;
        }
      }
    } else {
      // Cooldown - alternate between charging (bats) and orb bursts
      this.attackTimer += delta;
      if (this.attackTimer >= this.attackCooldown) {
        // Alternate: charge for bats, then orb burst
        if (Math.random() < 0.5) {
          this.startCharging();
        } else {
          this.startOrbBurst();
        }
      }
    }
  }

  /**
   * Update bats-only pattern
   */
  private updateBatsPattern(delta: number, phase: ScarecrowPhase): void {
    if (this.isCharging) {
      this.chargeTimer += delta;
      if (this.chargeTimer >= this.chargeDuration) {
        this.finishCharging(phase);
      }
    } else if (this.isSpawningBats) {
      this.batSpawnTimer += delta;
      if (this.batSpawnTimer >= this.batSpawnInterval) {
        this.batSpawnTimer = 0;
        this.spawnBat();
        this.batSpawnCount++;

        if (this.batSpawnCount >= this.batsToSpawn) {
          this.isSpawningBats = false;
          this.attackTimer = 0;
        }
      }
    } else {
      this.attackTimer += delta;
      if (this.attackTimer >= this.attackCooldown) {
        this.startCharging();
      }
    }
  }

  /**
   * Start an orb burst sequence
   */
  private startOrbBurst(): void {
    this.isOrbBursting = true;
    this.orbBurstCount = 0;
    this.orbBurstTimer = this.orbBurstDelay; // Fire immediately
  }

  /**
   * Shoot orbs from both hands
   * @param totalOrbs Total number of orbs to spawn (split between hands)
   */
  private shootOrbs(totalOrbs: number): void {
    console.log(`[Subjefelevel1] Shooting ${totalOrbs} orbs`);

    const leftHand = this.getFirePoint('leftHand');
    const rightHand = this.getFirePoint('rightHand');

    // Split orbs between hands (alternating)
    const orbsPerSide = Math.ceil(totalOrbs / 2);

    for (let i = 0; i < totalOrbs; i++) {
      const isLeft = i % 2 === 0;
      const hand = isLeft ? leftHand : rightHand;

      // Vary the angle slightly for each orb
      const baseAngle = isLeft ? 145 : 35;
      const angleVariation = (i - orbsPerSide / 2) * 15; // Spread orbs
      const travelAngle = baseAngle + angleVariation;

      const distance = Phaser.Math.Between(150, 350);
      const angleRad = Phaser.Math.DegToRad(travelAngle);

      const targetX = hand.x + Math.cos(angleRad) * distance;
      const targetY = hand.y - Math.sin(angleRad) * distance;

      this.scene.events.emit('spawnProjectile', {
        type: 'scarecoworb',
        x: hand.x,
        y: hand.y,
        angle: 0,
        allegiance: Allegiance.Enemy,
        shooterId: parseInt(this.entityId ?? '0'),
        travelTargetX: targetX,
        travelTargetY: targetY,
        travelSpeed: 400,
        travelStartScale: 0.05,
        travelEndScale: 0.2,
      });
    }
  }

  // ==================== BATS PATTERN ====================

  /**
   * Start charging animation before bat spawn
   */
  private startCharging(): void {
    console.log('[Subjefelevel1] Starting charge');
    this.isCharging = true;
    this.chargeTimer = 0;
    this.play(this.animKeys.charging, true);
  }

  /**
   * Finish charging and start spawning bats
   */
  private finishCharging(phase: ScarecrowPhase): void {
    console.log('[Subjefelevel1] Charge complete, spawning bats');
    this.isCharging = false;
    this.isSpawningBats = true;
    this.batSpawnCount = 0;
    this.batsToSpawn = phase.batCount ?? 1;
    this.batSpawnTimer = 0;
    this.nextBatHand = 'left';
  }

  /**
   * Spawn a single bat that will chase the player
   */
  private spawnBat(): void {
    const handKey = this.nextBatHand === 'left' ? 'leftHand' : 'rightHand';
    const point = this.getFirePoint(handKey);

    console.log(`[Subjefelevel1] Spawning bat from ${handKey}`);

    // Emit spawn event for BossBat (configured in entities-overrides)
    this.scene.events.emit('spawnEntity', {
      prefab: 'BossBat',
      x: point.x,
      y: point.y,
      config: {
        chaseTarget: true, // Flag to make bat chase player
      },
    });

    // Alternate hands
    this.nextBatHand = this.nextBatHand === 'left' ? 'right' : 'left';
  }

  // ==================== UTILITIES ====================

  /**
   * Get world position of a fire point, accounting for flip and scale
   */
  private getFirePoint(pointName: string): { x: number; y: number } {
    const point = this.firePoints[pointName] || { x: 0, y: 0 };
    const flipMultiplier = this.flipX ? -1 : 1;

    return {
      x: this.x + point.x * flipMultiplier * this.scaleX,
      y: this.y + point.y * this.scaleY,
    };
  }

  /**
   * Handle boss death
   */
  onDeath(): void {
    console.log('[Subjefelevel1] Boss defeated!');

    // Clear tint
    this.clearTint();

    // Disable physics body completely (no collisions during death fall)
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.enable = false;
    }

    // Get ground level from CollisionSystem (preferir suelo real, no techos/casas)
    const collisionSystem = this.scene.registry.get('collisionSystem') as
      | { getGroundYBelow?: (x: number, y: number) => number | null; getSolidYBelow?: (x: number, y: number) => number | null }
      | undefined;
    const groundY =
      collisionSystem?.getGroundYBelow?.(this.x, this.y) ??
      collisionSystem?.getSolidYBelow?.(this.x, this.y) ??
      (this.y + 200);

    // Ajuste para que el "piso" quede en la base del sprite segun su origin
    const targetY = groundY - (this.displayHeight * (1 - this.originY));

    // Tween to fall to ground smoothly
    this.scene.tweens.add({
      targets: this,
      y: targetY,
      duration: 400,
      ease: 'Bounce.easeOut',
    });

    // Play death animation
    this.play(this.animKeys.death, true);

    // Drop powerup at current position (will fall with boss via the tween)
    this.scene.events.emit('spawnDrop', {
      type: 'Updoublelaser',
      x: this.x,
      y: groundY, // Spawn at ground level
      data: { ammo: 200 },
    });

    // Destroy all DestructableHay objects
    this.scene.events.emit('destroyObjectsOfType', {
      type: 'DestructableHay',
    });

    // Call parent (emits bossDefeated event)
    super.onDeath();
  }
}
